import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { LuEuro } from 'react-icons/lu';

import type { CurrencyInputProps } from './types';
import {
    filterCurrencyEdit,
    formatCurrency,
    getCaretPosition,
    groupCurrencyInput,
    GROUPING_SEPARATOR,
    parseCurrency,
} from './util';
import { Label } from '../../general/Label';
import { cn } from '../../utils/cn';

export type { CurrencyInputProps } from './types';

interface EditState {
    caret: number | null;
    value: string;
}

/**
 * A form currency input component with Euro symbol suffix and German locale formatting.
 *
 * @param id - ID of the input element. Used for htmlFor association.
 * @param value - The current currency value as a number.
 * @param onChange - Callback fired when the value changes.
 * @param label - Label text for the input.
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param error - Error message to display below the input.
 * @param min - Minimum allowed value.
 * @param max - Maximum allowed value.
 * @param placeholder - Placeholder text for the input.
 * @param disabled - Whether the input is disabled.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the input wrapper.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Basic usage
 * <CurrencyInput label="Betrag" id="amount" value={amount} onChange={setAmount} />
 *
 * @example
 * // With min/max constraints
 * <CurrencyInput label="Preis" id="price" min={0} max={10000} value={price} onChange={setPrice} />
 *
 * @example
 * // With error state
 * <CurrencyInput label="Betrag" id="amount" value={amount} onChange={setAmount} error="Ungültiger Betrag" />
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
    className,
    disabled,
    error,
    id,
    inputClassName,
    label,
    labelClassName,
    layout = 'horizontal',
    max,
    min,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder = '0,00',
    value,
    ...inputProps
}) => {
    const isHorizontal = layout === 'horizontal';
    const hasError = Boolean(error);

    const [editState, setEditState] = useState<EditState>(() => ({ caret: null, value: formatCurrency(value) }));
    const isFocusedRef = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const displayValue = editState.value;

    useEffect(() => {
        if (isFocusedRef.current) return;

        const formattedValue = formatCurrency(value);
        setEditState((current) =>
            current.value === formattedValue ? current : { caret: null, value: formattedValue },
        );
    }, [value]);

    /**
     * React rewrites the value of a controlled input once more after the event was dispatched
     * (restoreStateOfTarget), which leaves the caret at the end. Reapplying the caret in a
     * microtask therefore has to happen on top of the layout effect, but still runs before paint.
     */
    const applyCaret = useCallback((expectedValue: string, caret: number) => {
        const input = inputRef.current;
        if (!input || input.value !== expectedValue) return;
        if (input.selectionStart === caret && input.selectionEnd === caret) return;

        input.setSelectionRange(caret, caret);
    }, []);

    useLayoutEffect(() => {
        const { caret, value: editedValue } = editState;
        if (caret === null) return;

        applyCaret(editedValue, caret);
        queueMicrotask(() => applyCaret(editedValue, caret));
    }, [applyCaret, editState]);

    const applyEdit = useCallback(
        (rawValue: string, rawCaret: number) => {
            if (rawValue === '') {
                setEditState({ caret: 0, value: '' });
                onChange?.(null);
                return;
            }

            const filteredValue = filterCurrencyEdit(rawValue, displayValue);
            const groupedValue = groupCurrencyInput(filteredValue);

            setEditState({ caret: getCaretPosition(groupedValue, rawValue, rawCaret), value: groupedValue });

            const numericValue = parseCurrency(filteredValue);
            if (numericValue !== null) {
                onChange?.(numericValue);
            }
        },
        [displayValue, onChange],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            applyEdit(e.target.value, e.target.selectionStart ?? e.target.value.length);
        },
        [applyEdit],
    );

    /**
     * Removing a thousands separator alone would not change the value, so backspace and delete
     * take the digit on the other side of it instead of leaving the keystroke without effect.
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const { selectionEnd, selectionStart, value: inputValue } = e.currentTarget;

            if (selectionStart !== null && selectionStart === selectionEnd) {
                if (e.key === 'Backspace' && inputValue[selectionStart - 1] === GROUPING_SEPARATOR) {
                    e.preventDefault();
                    applyEdit(
                        `${inputValue.slice(0, selectionStart - 2)}${inputValue.slice(selectionStart - 1)}`,
                        selectionStart - 2,
                    );
                } else if (e.key === 'Delete' && inputValue[selectionStart] === GROUPING_SEPARATOR) {
                    e.preventDefault();
                    applyEdit(
                        `${inputValue.slice(0, selectionStart + 1)}${inputValue.slice(selectionStart + 2)}`,
                        selectionStart,
                    );
                }
            }

            onKeyDown?.(e);
        },
        [applyEdit, onKeyDown],
    );

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            isFocusedRef.current = true;
            onFocus?.(e);
        },
        [onFocus],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            isFocusedRef.current = false;

            let numericValue = parseCurrency(displayValue);

            if (numericValue !== null) {
                if (min !== undefined && numericValue < min) {
                    numericValue = min;
                }

                if (max !== undefined && numericValue > max) {
                    numericValue = max;
                }

                setEditState({ caret: null, value: formatCurrency(numericValue) });
                onChange?.(numericValue);
            } else if (displayValue === '') {
                onChange?.(null);
            } else {
                setEditState({ caret: null, value: formatCurrency(value) });
            }

            onBlur?.(e);
        },
        [displayValue, min, max, onBlur, onChange, value],
    );

    return (
        <div
            className={cn(
                // Native <input size=20> sets ~20ch min-width; min-w-0 lets flex honor narrow rows.
                'flex min-w-0',
                isHorizontal ? 'flex-row gap-2.5 items-start' : 'flex-col gap-2 items-start',
                className,
            )}
        >
            {label && (
                <Label
                    htmlFor={id}
                    className={cn(
                        isHorizontal ? 'w-[125px] shrink-0 justify-end h-8' : 'w-full px-0 py-0 justify-start',
                        labelClassName,
                    )}
                >
                    {label}
                </Label>
            )}
            <div className={cn('flex min-w-0 flex-col', isHorizontal ? 'flex-1' : 'w-full')}>
                <div
                    className={cn(
                        'h-[32px] w-full min-w-0',
                        'flex items-center gap-2',
                        'pl-3',
                        'bg-white border rounded-[4px]',
                        'font-normal font-roboto leading-[1.46]',
                        hasError ? 'border-alert-error' : 'border-grey-dark',
                        'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
                        disabled && 'cursor-not-allowed bg-surface-light',
                        inputClassName,
                    )}
                >
                    <input
                        {...inputProps}
                        ref={inputRef}
                        id={id}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        data-1p-ignore
                        disabled={disabled}
                        value={displayValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        className={cn(
                            'flex-1 min-w-0 h-full',
                            'bg-transparent border-none outline-none',
                            'text-sm text-midnight',
                            'placeholder:text-midnight-placeholder',
                            'disabled:cursor-not-allowed disabled:bg-surface-light disabled:opacity-50',
                        )}
                    />
                    <div className="flex items-center ml-auto shrink-0 h-full">
                        <span
                            className="border-l border-grey-dark h-4/6"
                            aria-hidden="true"
                            data-testid="currency-input-divider"
                        />
                        <span
                            className="flex items-center justify-center h-5 w-8 shrink-0 text-midnight select-none"
                            aria-hidden="true"
                        >
                            <LuEuro
                                strokeWidth={2}
                                className="size-[17px] shrink-0"
                                data-testid="currency-input-euro-icon"
                            />
                        </span>
                    </div>
                </div>
                {hasError && <span className="text-[10px] leading-[1.46] text-alert-error mt-1">{error}</span>}
            </div>
        </div>
    );
};
